import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import {computeFrame, displayScale, clampOffset, zoomBounds, cropRect, outputSize} from './cropMath';
import './ImageCropperDialog.scss';

// Zoom 1 = image exactly covers the frame. The user can zoom out below that
// (down to the whole image fitting inside the frame, padded on the card).
const INITIAL_ZOOM = 1;
const ZOOM_STEP = 1.2;

// Lets the user pan/zoom an image behind a fixed crop frame (the lesson-card
// thumbnail ratio). The whole image stays visible on a dimmed stage; only the
// framed area is exported as a base64 data URL. All geometry lives in
// cropMath.js.
export default function ImageCropperDialog({open, src, onCancel, onConfirm, style}) {
    const stageRef = React.useRef(null);
    const imgRef = React.useRef(null);
    const dragRef = React.useRef(null);
    const zoomByRef = React.useRef(null);
    const [stage, setStage] = React.useState(null);
    const [imgSize, setImgSize] = React.useState(null);
    const [zoom, setZoom] = React.useState(INITIAL_ZOOM);
    const [offset, setOffset] = React.useState({x: 0, y: 0});
    const [flip, setFlip] = React.useState({x: false, y: false});
    const [rot, setRot] = React.useState(0);
    const [dragging, setDragging] = React.useState(false);

    React.useEffect(() => {
        setImgSize(null);
        setZoom(INITIAL_ZOOM);
        setOffset({x: 0, y: 0});
        setFlip({x: false, y: false});
        setRot(0);
    }, [src]);

    const frame = stage ? computeFrame(stage) : null;
    const ready = Boolean(imgSize && frame);
    // All geometry runs on the rotated ("oriented") dimensions — a 90°/270°
    // turn swaps width and height.
    const orientedFor = (deg) => (deg % 180 ? {w: imgSize.h, h: imgSize.w} : imgSize);
    const oriented = imgSize ? orientedFor(rot) : null;
    const bounds = ready ? zoomBounds(oriented, frame) : {min: INITIAL_ZOOM, max: INITIAL_ZOOM};
    const clamp = (off, z) => clampOffset(off, z, oriented, frame);

    const measureStage = () => {
        const el = stageRef.current;
        if (el && el.clientWidth && el.clientHeight)
            setStage({w: el.clientWidth, h: el.clientHeight});
    };

    React.useEffect(() => {
        if (!open) return;
        window.addEventListener('resize', measureStage);
        return () => window.removeEventListener('resize', measureStage);
    }, [open]);

    const applyZoom = (rawZoom) => {
        if (!ready) return;
        const newZoom = Math.min(bounds.max, Math.max(bounds.min, rawZoom));
        setOffset(clamp({x: offset.x * newZoom / zoom, y: offset.y * newZoom / zoom}, newZoom));
        setZoom(newZoom);
    };

    const zoomBy = (factor) => applyZoom(zoom * factor);
    zoomByRef.current = zoomBy;

    // Native listener: React's onWheel can't preventDefault (passive), and the
    // page must not scroll while zooming over the image.
    React.useEffect(() => {
        if (!open) return;
        const el = stageRef.current;
        if (!el) return;
        const onWheel = (e) => {
            e.preventDefault();
            zoomByRef.current(Math.exp(-e.deltaY * 0.0015));
        };
        el.addEventListener('wheel', onWheel, {passive: false});
        return () => el.removeEventListener('wheel', onWheel);
    }, [open, src]);

    const startDrag = (e) => {
        if (!ready) return;
        e.preventDefault();
        if (e.currentTarget.setPointerCapture) {
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* unsupported */ }
        }
        dragRef.current = {x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y};
        setDragging(true);
    };

    const moveDrag = (e) => {
        const d = dragRef.current;
        if (!d) return;
        setOffset(clamp({x: d.ox + e.clientX - d.x, y: d.oy + e.clientY - d.y}, zoom));
    };

    const endDrag = () => {
        dragRef.current = null;
        setDragging(false);
    };

    const handleImageLoad = (e) => {
        measureStage();
        setImgSize({w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight});
    };

    const rotate = () => {
        if (!ready) return;
        const next = (rot + 90) % 360;
        const b = zoomBounds(orientedFor(next), frame);
        setRot(next);
        setOffset({x: 0, y: 0});
        setZoom(Math.min(b.max, Math.max(b.min, zoom)));
    };

    const handleConfirm = () => {
        if (!ready || !imgRef.current) return;
        const {sx, sy, sw, sh} = cropRect(oriented, frame, zoom, offset);
        const out = outputSize(sw, sh);
        const canvas = document.createElement('canvas');
        canvas.width = out.w;
        canvas.height = out.h;
        const ctx = canvas.getContext('2d');
        const isPng = typeof src === 'string' && src.indexOf('data:image/png') === 0;
        if (!isPng) {
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, out.w, out.h);
        }
        // The crop rect is in preview (rotated + flipped) coordinates. Rebuild
        // that view on the canvas — map the crop rect to the output, then
        // apply the same flip and rotation the preview shows — and draw the
        // original image through it.
        ctx.scale(out.w / sw, out.h / sh);
        ctx.translate(-sx, -sy);
        ctx.translate(flip.x ? oriented.w : 0, flip.y ? oriented.h : 0);
        ctx.scale(flip.x ? -1 : 1, flip.y ? -1 : 1);
        if (rot === 90) {
            ctx.translate(oriented.w, 0);
            ctx.rotate(Math.PI / 2);
        } else if (rot === 180) {
            ctx.translate(oriented.w, oriented.h);
            ctx.rotate(Math.PI);
        } else if (rot === 270) {
            ctx.translate(0, oriented.h);
            ctx.rotate(Math.PI * 1.5);
        }
        ctx.drawImage(imgRef.current, 0, 0);
        onConfirm(canvas.toDataURL(isPng ? 'image/png' : 'image/jpeg', 0.92));
    };

    let imgStyle = {visibility: 'hidden'};
    if (ready) {
        const s = displayScale(oriented, frame, zoom);
        imgStyle = {
            width: imgSize.w * s,
            height: imgSize.h * s,
            transform: `translate(${offset.x}px, ${offset.y}px) translate(-50%, -50%)`
                + ` scale(${flip.x ? -1 : 1}, ${flip.y ? -1 : 1}) rotate(${rot}deg)`,
        };
    }

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="md" className="ImageCropperDialog" style={style}>
            <div className="cropper-container">
                <div className="cropper-title">Adjust Photo</div>
                <div className="cropper-hint">
                    Drag the image to reposition it and use the slider to zoom. The bright framed area is what will show on the card.
                </div>
                <div
                    className={`cropper-stage${dragging ? ' dragging' : ''}`}
                    ref={stageRef}
                    onPointerDown={startDrag}
                    onPointerMove={moveDrag}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                >
                    {src && (
                        <img
                            ref={imgRef}
                            src={src}
                            alt=""
                            draggable={false}
                            onLoad={handleImageLoad}
                            style={imgStyle}
                        />
                    )}
                    {frame && (
                        <div className="cropper-frame" style={{width: frame.w, height: frame.h}}/>
                    )}
                </div>
                <div className="cropper-zoom">
                    <span className="cropper-zoom__label" onClick={() => zoomBy(1 / ZOOM_STEP)}>&minus;</span>
                    <input
                        type="range"
                        min={bounds.min}
                        max={bounds.max}
                        step={0.01}
                        value={zoom}
                        onChange={(e) => applyZoom(Number(e.target.value))}
                    />
                    <span className="cropper-zoom__label" onClick={() => zoomBy(ZOOM_STEP)}>+</span>
                    <div
                        className={`cropper-tool cropper-tool--rotate${rot ? ' active' : ''}`}
                        title="Rotate 90°"
                        onClick={rotate}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10"/>
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                        </svg>
                        {rot ? <span>{rot}&deg;</span> : null}
                    </div>
                    <div
                        className={`cropper-tool cropper-tool--flip-h${flip.x ? ' active' : ''}`}
                        title="Flip horizontally"
                        onClick={() => setFlip({...flip, x: !flip.x})}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="8 7 3 12 8 17"/>
                            <polyline points="16 7 21 12 16 17"/>
                            <line x1="12" y1="3" x2="12" y2="21" strokeDasharray="2 3"/>
                        </svg>
                    </div>
                    <div
                        className={`cropper-tool cropper-tool--flip-v${flip.y ? ' active' : ''}`}
                        title="Flip vertically"
                        onClick={() => setFlip({...flip, y: !flip.y})}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="7 8 12 3 17 8"/>
                            <polyline points="7 16 12 21 17 16"/>
                            <line x1="3" y1="12" x2="21" y2="12" strokeDasharray="2 3"/>
                        </svg>
                    </div>
                </div>
                <div className="cropper-actions">
                    <div className="button-new negative" onClick={onCancel}>Cancel</div>
                    <div className={`button-new positive${!ready ? ' inactive' : ''}`} onClick={handleConfirm}>Save Photo</div>
                </div>
            </div>
        </Dialog>
    );
}
